import React, { useRef, useEffect } from 'react';
import { Bold, Italic, Underline, List, ListOrdered, Link, Image, Type } from 'lucide-react';

const RichTextEditor = ({ value, onChange, className = '' }) => {
  const editorRef = useRef(null);

  useEffect(() => {
    if (editorRef.current && value && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value;
    }
  }, []);

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const executeCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  };

  const insertHeading = (level) => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const heading = document.createElement(`h${level}`);
      heading.innerHTML = selection.toString() || `Heading ${level}`;
      range.deleteContents();
      range.insertNode(heading);
      handleInput();
    }
  };

  const insertLink = () => {
    const url = prompt('Enter URL:');
    if (url) {
      executeCommand('createLink', url);
    }
  };

  const insertImage = () => {
    const url = prompt('Enter image URL:');
    if (url) {
      executeCommand('insertImage', url);
    }
  };

  return (
    <div className={`border border-border ${className}`}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 p-2 border-b border-border bg-secondary/30">
        <button
          type="button"
          onClick={() => insertHeading(1)}
          className="p-2 hover:bg-secondary transition-colors"
          title="Heading 1"
        >
          <Type className="w-5 h-5" />
          <span className="text-xs ml-1">H1</span>
        </button>
        <button
          type="button"
          onClick={() => insertHeading(2)}
          className="p-2 hover:bg-secondary transition-colors"
          title="Heading 2"
        >
          <Type className="w-4 h-4" />
          <span className="text-xs ml-1">H2</span>
        </button>
        <button
          type="button"
          onClick={() => insertHeading(3)}
          className="p-2 hover:bg-secondary transition-colors"
          title="Heading 3"
        >
          <Type className="w-3 h-3" />
          <span className="text-xs ml-1">H3</span>
        </button>
        
        <div className="w-px h-6 bg-border mx-1" />
        
        <button
          type="button"
          onClick={() => executeCommand('bold')}
          className="p-2 hover:bg-secondary transition-colors"
          title="Bold"
        >
          <Bold className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => executeCommand('italic')}
          className="p-2 hover:bg-secondary transition-colors"
          title="Italic"
        >
          <Italic className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => executeCommand('underline')}
          className="p-2 hover:bg-secondary transition-colors"
          title="Underline"
        >
          <Underline className="w-4 h-4" />
        </button>
        
        <div className="w-px h-6 bg-border mx-1" />
        
        <button
          type="button"
          onClick={() => executeCommand('insertUnorderedList')}
          className="p-2 hover:bg-secondary transition-colors"
          title="Bullet List"
        >
          <List className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => executeCommand('insertOrderedList')}
          className="p-2 hover:bg-secondary transition-colors"
          title="Numbered List"
        >
          <ListOrdered className="w-4 h-4" />
        </button>
        
        <div className="w-px h-6 bg-border mx-1" />
        
        <button
          type="button"
          onClick={insertLink}
          className="p-2 hover:bg-secondary transition-colors"
          title="Insert Link"
        >
          <Link className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={insertImage}
          className="p-2 hover:bg-secondary transition-colors"
          title="Insert Image"
        >
          <Image className="w-4 h-4" />
        </button>
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        className="p-4 min-h-[400px] focus:outline-none prose prose-lg max-w-none"
        style={{
          fontSize: '16px',
          lineHeight: '1.75'
        }}
        data-testid="rich-text-editor-content"
      />
    </div>
  );
};

export default RichTextEditor;
